import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertCircle, CheckCircle, TrendingUp, RefreshCw } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

interface QualityScore {
  completeness_score: number;
  missing_fields: string[];
  suggestions: string[];
  last_analyzed_at: string;
}

interface PropertyQualityScoreProps {
  propertyId: string;
  autoAnalyze?: boolean;
}

export const PropertyQualityScore: React.FC<PropertyQualityScoreProps> = ({ 
  propertyId, 
  autoAnalyze = false 
}) => {
  const [qualityScore, setQualityScore] = useState<QualityScore | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { toast } = useToast();

  const fetchQualityScore = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('property_quality_scores')
        .select('*')
        .eq('property_id', propertyId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching quality score:', error);
        return;
      }

      if (data) {
        setQualityScore({
          completeness_score: data.completeness_score,
          missing_fields: Array.isArray(data.missing_fields) ? data.missing_fields.filter(f => typeof f === 'string') : [],
          suggestions: Array.isArray(data.suggestions) ? data.suggestions.filter(s => typeof s === 'string') : [],
          last_analyzed_at: data.last_analyzed_at
        });
      } else if (autoAnalyze) {
        await analyzeProperty();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const analyzeProperty = async () => {
    try {
      setIsAnalyzing(true);
      
      const { data, error } = await supabase.functions.invoke('analyze-property-quality', {
        body: { property_id: propertyId }
      });

      if (error) {
        console.error('Error analyzing property:', error);
        toast({
          title: "Erro na análise",
          description: "Não foi possível analisar a qualidade do imóvel",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setQualityScore({
          completeness_score: data.completeness_score,
          missing_fields: data.missing_fields,
          suggestions: data.suggestions,
          last_analyzed_at: data.analysis_date
        });

        toast({
          title: "Análise concluída",
          description: `Score de qualidade: ${data.completeness_score}%`,
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Erro",
        description: "Erro interno do servidor",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 80) return 'default';
    if (score >= 60) return 'secondary';
    return 'destructive';
  };

  useEffect(() => {
    fetchQualityScore();
  }, [propertyId]);

  if (isLoading && !qualityScore) {
    return (
      <Card className="p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/3 mb-4"></div>
          <div className="h-8 bg-muted rounded w-1/4 mb-4"></div>
          <div className="space-y-2">
            <div className="h-3 bg-muted rounded"></div>
            <div className="h-3 bg-muted rounded w-3/4"></div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Score de Qualidade
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={analyzeProperty}
          disabled={isAnalyzing}
        >
          {isAnalyzing ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isAnalyzing ? 'Analisando...' : 'Analisar'}
        </Button>
      </div>

      {qualityScore ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-3xl font-bold ${getScoreColor(qualityScore.completeness_score)}`}>
                {qualityScore.completeness_score}%
              </div>
              <div className="text-sm text-muted-foreground">
                Completude do anúncio
              </div>
            </div>
            <Badge variant={getScoreBadgeVariant(qualityScore.completeness_score)}>
              {qualityScore.completeness_score >= 80 ? 'Excelente' : 
               qualityScore.completeness_score >= 60 ? 'Bom' : 'Precisa melhorar'}
            </Badge>
          </div>

          {qualityScore.missing_fields.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                Campos faltando
              </h4>
              <div className="flex flex-wrap gap-1">
                {qualityScore.missing_fields.map((field, index) => (
                  <Badge key={index} variant="outline" className="text-xs">
                    {field}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {qualityScore.suggestions.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-500" />
                Sugestões de melhoria
              </h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                {qualityScore.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    {suggestion}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            Última análise: {new Date(qualityScore.last_analyzed_at).toLocaleString('pt-BR')}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="text-muted-foreground mb-4">
            Nenhuma análise disponível
          </div>
          <Button onClick={analyzeProperty} disabled={isAnalyzing}>
            {isAnalyzing ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <TrendingUp className="h-4 w-4 mr-2" />
            )}
            {isAnalyzing ? 'Analisando...' : 'Fazer análise'}
          </Button>
        </div>
      )}
    </Card>
  );
};